import type { EntityType } from '@/core/graphql/generated/types';
import { getDefaultCountryIso } from '@/utils/formatters/phoneCountryCode';

import { getCollection, getFieldEntries } from '../fields/registry';
import type {
	CollectionId,
	CommonFieldDef,
	ItemFieldDef
} from '../fields/types';
import {
	findActiveOptionId,
	getPicklistOptions,
	type Picklists
} from '../picklists';

export const EMPTY_MULTI_PICKLIST_ROW = {
	id: '',
	name: '',
	isPrimary: true
};

const emptyValueFor = (def: Pick<CommonFieldDef, 'kind'>): unknown => {
	switch (def.kind) {
		case 'year':
		case 'boolean':
			return undefined;
		case 'multiPicklist':
			return [{ ...EMPTY_MULTI_PICKLIST_ROW }];
		case 'collection':
			return [];
		default:
			return '';
	}
};

export const buildEntityDefaults = (
	entityType: EntityType,
	overrides: Record<string, unknown> = {}
): Record<string, unknown> => ({
	...Object.fromEntries(
		getFieldEntries(entityType).map(({ key, def }) => [key, emptyValueFor(def)])
	),
	...overrides
});

type ItemContext = {
	countryId: string | null | undefined;
	picklists: Partial<Picklists>;
};

const resolveItemDefault = (def: ItemFieldDef, ctx: ItemContext): unknown => {
	switch (def.defaultValue?.type) {
		case 'entityCountry':
			return ctx.countryId ?? '';
		case 'activeOption':
			return def.picklist
				? (findActiveOptionId(
						getPicklistOptions(ctx.picklists, def.picklist)
					) ?? '')
				: '';
		case 'value':
			return def.defaultValue.value;
		default:
			return emptyValueFor(def);
	}
};

export const buildEmptyItem = (
	collectionId: CollectionId,
	ctx: ItemContext
): Record<string, unknown> => {
	const { fields } = getCollection(collectionId);
	const countryName = ctx.picklists.countryOptions?.find(
		c => c.id === ctx.countryId
	)?.name;

	const item: Record<string, unknown> = {};

	Object.entries(fields).forEach(([key, def]) => {
		item[key] = resolveItemDefault(def, ctx);
		if (def.isoKey) item[def.isoKey] = getDefaultCountryIso(countryName);
	});

	return item;
};
